/**
 * TurnScriptPicker — the turn picker under the scripted turn lines (UI-SPEC §2.21).
 *
 * Head: « Tour » + squares 1..max, then the four sources (Mes prompts · Objections · Questions
 * avant achat · Texte libre). Each item is stored verbatim; ≤ 5 per turn (the save 400s more).
 * Turn 1 « Mes prompts » is filtered on the starting intent's axis until the user removes it.
 */
import { useState } from 'react';
import { Box, Button, Checkbox, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BrandQuery, ConversationIntentId } from '@traaker/shared';
import { CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS, CONVERSATION_MAX_TURN_OPTIONS } from '@traaker/shared';
import { AXIS_COLOR, AXIS_ORDER } from '../promptStudioModel';
import { intentAxis, matches, objectionsFor, promptsForTurn, scriptedTurns, toggleItem } from './conversationModel';
import type { ConvItem } from './conversationModel';

type Source = 'prompt' | 'objection' | 'question' | 'text';
const SOURCES: Source[] = ['prompt', 'objection', 'question', 'text'];
const SOURCE_KEY: Record<Source, string> = {
  prompt: 'promptStudio.conv.src.pr',
  objection: 'promptStudio.conv.src.ob',
  question: 'promptStudio.conv.src.qa',
  text: 'promptStudio.conv.src.tx',
};
/** Lists render at most 40 rows (UI-SPEC §2.21). */
const MAX_LISTED = 40;

export interface TurnScriptPickerProps {
  turn: number;
  maxTurns: number;
  items: ConvItem[];
  intentId: ConversationIntentId | null;
  queries: BrandQuery[];
  personaObjections?: string[];
  brandObjections?: string[];
  customerQuestions?: string[];
  onTurn: (turn: number) => void;
  onItems: (items: ConvItem[]) => void;
  onDone: () => void;
}

export function TurnScriptPicker({
  turn,
  maxTurns,
  items,
  intentId,
  queries,
  personaObjections,
  brandObjections,
  customerQuestions,
  onTurn,
  onItems,
  onDone,
}: TurnScriptPickerProps) {
  const { t } = useTranslation();
  const [source, setSource] = useState<Source>('prompt');
  const [q, setQ] = useState('');
  const [onIntent, setOnIntent] = useState(true);
  const [draft, setDraft] = useState('');
  const scripted = new Set(scriptedTurns(items));
  const mine = items.filter((i) => i.turn === turn);
  const full = mine.length >= CONVERSATION_MAX_TURN_OPTIONS;
  const has = (text: string) => mine.some((i) => i.text === text);
  const toggle = (item: Omit<ConvItem, 'turn'>) => onItems(toggleItem(items, { ...item, turn }));

  // Rows of the current source: { key, text, right?, dot?, item }
  type Row = { key: string; text: string; right?: string; dot?: string; item: Omit<ConvItem, 'turn'> };
  let rows: Row[] = [];
  let empty = '';
  let searchKey = '';
  if (source === 'prompt') {
    const listed = promptsForTurn(queries, intentId, turn, '', onIntent);
    // Selected first, then axis order.
    rows = listed
      .map((r) => ({ key: r.queryId, text: r.text, dot: AXIS_COLOR[r.axis as keyof typeof AXIS_COLOR], tags: r.tags ?? [], axis: r.axis,
        item: { text: r.text, source: 'prompt' as const, refId: r.queryId } }))
      .filter((r) => !q.trim() || matches(r.text, q) || r.tags.some((x) => matches(x, q)))
      .sort((a, b) => Number(has(b.text)) - Number(has(a.text)) || AXIS_ORDER.indexOf(a.axis as never) - AXIS_ORDER.indexOf(b.axis as never));
    searchKey = 'promptStudio.conv.searchPrompt';
  } else if (source === 'objection') {
    rows = objectionsFor(personaObjections, brandObjections).map((o) => ({
      key: o.text, text: o.text,
      right: t(o.from === 'persona' ? 'promptStudio.conv.fromPersona' : 'promptStudio.conv.fromKnowledge'),
      item: { text: o.text, source: 'objection' as const },
    }));
    empty = t('promptStudio.conv.noObjections');
    searchKey = 'promptStudio.conv.searchObjection';
  } else if (source === 'question') {
    rows = (customerQuestions ?? []).filter((x) => x.trim()).map((text) => ({ key: text, text, item: { text, source: 'question' as const } }));
    empty = t('promptStudio.conv.noQuestions');
    searchKey = 'promptStudio.conv.searchQuestion';
  }
  const total = rows.length;
  if (source !== 'prompt' && q.trim()) rows = rows.filter((r) => matches(r.text, q));
  // An item longer than a scripted item may be is not offered (the save 400s it).
  rows = rows.filter((r) => r.text.length <= CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS);

  const addDraft = () => {
    const text = draft.trim();
    if (!text || has(text) || full) return;
    toggle({ text, source: 'text' });
    setDraft('');
  };
  // « Fermer » keeps a free text still being typed, as Entrée would (dev 2026-11-02: it was lost).
  const close = () => {
    const text = draft.trim();
    if (source === 'text' && text && !has(text) && !full) onItems(toggleItem(items, { turn, text, source: 'text' }));
    onDone();
  };

  const foot =
    mine.length === 0 ? t('promptStudio.conv.pick0') : mine.length === 1 ? t('promptStudio.conv.pick1') : t('promptStudio.conv.pickN', { count: mine.length });
  const square = { width: 28, height: 28, borderRadius: 1, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', p: 0 };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25, mt: 1, bgcolor: 'background.paper' }}>
      {/* Head: turn squares + sources */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mr: 0.5 }}>{t('promptStudio.conv.turns')}</Box>
        {Array.from({ length: maxTurns }, (_, i) => i + 1).map((n) => (
          <Box key={n} component="button" onClick={() => onTurn(n)}
            title={`${t('promptStudio.conv.turnN', { n })}${scripted.has(n) ? t('promptStudio.conv.turnScripted') : ''}`}
            sx={{ ...square, borderColor: n === turn ? '#7c3aed' : 'divider', bgcolor: n === turn ? '#7c3aed' : scripted.has(n) ? 'rgba(124,58,237,.08)' : 'transparent', color: n === turn ? '#fff' : 'text.primary' }}>
            {n}
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        {SOURCES.map((s) => (
          <Box key={s} component="button" onClick={() => { setSource(s); setQ(''); }}
            sx={{ fontSize: 12, border: '1px solid', borderColor: s === source ? '#7c3aed' : 'divider', borderRadius: 1, px: 1, py: 0.3,
              bgcolor: s === source ? 'rgba(124,58,237,.08)' : 'transparent', color: s === source ? '#7c3aed' : 'text.secondary', cursor: 'pointer' }}>
            {t(SOURCE_KEY[s])}
          </Box>
        ))}
      </Box>

      {/* Selected items */}
      {mine.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {mine.map((i) => (
            <Box key={i.text} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.75, py: 0.25, maxWidth: 300 }}>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.text}</Box>
              <Box component="button" onClick={() => toggle(i)} sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: 'text.secondary' }}>✕</Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Turn-1 intent filter line */}
      {source === 'prompt' && turn === 1 && intentId && (
        <Box sx={{ fontSize: 11.5, color: 'text.secondary', mt: 1 }}>
          {onIntent ? (
            <>
              {t('promptStudio.conv.filteredOnIntent')} • {t(`promptStudio.axis.${intentAxis(intentId)}`)}{' '}
              <Box component="button" title={t('promptStudio.conv.removeFilter')} onClick={() => setOnIntent(false)}
                sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: 'text.secondary' }}>✕</Box>
            </>
          ) : (
            <Box component="button" onClick={() => setOnIntent(true)} sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: '#7c3aed', fontSize: 11.5 }}>
              {t('promptStudio.conv.filterOnIntent')}
            </Box>
          )}
        </Box>
      )}

      {/* List / free text */}
      {source === 'text' ? (
        <Box component="input" value={draft} maxLength={CONVERSATION_MAX_SCRIPTED_QUESTION_CHARS} placeholder={t('promptStudio.conv.freeText')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
          sx={{ width: '100%', mt: 1, fontSize: 13, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.75, outline: 'none', fontFamily: 'inherit', '&:focus': { borderColor: '#7c3aed' } }} />
      ) : (
        <>
          {total > 6 && (
            <Box component="input" value={q} placeholder={t(searchKey)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
              sx={{ width: '100%', mt: 1, fontSize: 12, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5, outline: 'none', fontFamily: 'inherit' }} />
          )}
          <Box sx={{ maxHeight: 220, overflowY: 'auto', mt: 0.5 }}>
            {rows.length === 0 && (
              <Box sx={{ fontSize: 12, color: 'text.disabled', py: 0.75 }}>
                {q.trim() ? t('promptStudio.conv.noMatch', { q }) : empty}
              </Box>
            )}
            {rows.slice(0, MAX_LISTED).map((r) => {
              const checked = has(r.text);
              return (
                <Tooltip key={r.key} title={!checked && full ? t('promptStudio.conv.turnFull') : ''} placement="left">
                  <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, cursor: 'pointer', opacity: !checked && full ? 0.5 : 1 }}>
                    <Checkbox size="small" checked={checked} disabled={!checked && full} onChange={() => toggle(r.item)}
                      sx={{ p: 0.25, '&.Mui-checked': { color: '#7c3aed' } }} />
                    {r.dot && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: r.dot, flexShrink: 0 }} />}
                    <Box sx={{ flex: 1, fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.text}</Box>
                    {r.right && <Box sx={{ fontSize: 11, color: 'text.disabled', ml: 0.5 }}>{r.right}</Box>}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* Foot */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Box sx={{ fontSize: 11.5, color: 'text.secondary', flex: 1 }}>{foot}</Box>
        <Button variant="contained" size="small" onClick={close} sx={{ fontWeight: 600 }}>{t('promptStudio.conv.close')}</Button>
      </Box>
    </Box>
  );
}
